import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Calendar } from 'lucide-react';
import { format, subDays, subMonths, subYears, startOfDay, endOfDay, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';

interface DateRangeFilterProps {
  onDateRangeChange: (startDate: Date, endDate: Date) => void;
  defaultPreset?: 'today' | 'week' | 'month' | 'year' | 'custom';
}

export function DateRangeFilter({ onDateRangeChange, defaultPreset = 'week' }: DateRangeFilterProps) {
  const [preset, setPreset] = useState<'today' | 'week' | 'month' | 'year' | 'custom'>(defaultPreset);
  const [customStart, setCustomStart] = useState<string>(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [customEnd, setCustomEnd] = useState<string>(format(new Date(), 'yyyy-MM-dd'));

  const getDateRange = (p: string): [Date, Date] => {
    const now = new Date();
    
    switch (p) {
      case 'today':
        return [startOfDay(now), endOfDay(now)];
      case 'week':
        return [startOfDay(subDays(now, 7)), endOfDay(now)];
      case 'month':
        return [startOfMonth(subMonths(now, 1)), endOfMonth(now)];
      case 'year':
        return [startOfYear(subYears(now, 1)), endOfYear(now)];
      case 'custom':
        return [
          startOfDay(new Date(customStart)),
          endOfDay(new Date(customEnd))
        ];
      default:
        return [startOfDay(subDays(now, 7)), endOfDay(now)];
    }
  };

  const handlePresetChange = (newPreset: string) => {
    setPreset(newPreset as any);
    const [start, end] = getDateRange(newPreset);
    onDateRangeChange(start, end);
  };

  const handleCustomDateChange = (type: 'start' | 'end', value: string) => {
    if (type === 'start') {
      setCustomStart(value);
    } else {
      setCustomEnd(value);
    }

    if (customStart && customEnd) {
      const [start, end] = getDateRange('custom');
      onDateRangeChange(start, end);
    }
  };

  const [start, end] = getDateRange(preset);

  return (
    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-slate-900 dark:to-slate-800 border-blue-200 dark:border-slate-700">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <div>
            <CardTitle className="text-base text-foreground">Time Range Filter</CardTitle>
            <CardDescription className="text-muted-foreground">View data from historical period</CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Preset buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {[
            { label: 'Today', value: 'today' },
            { label: 'Last 7 Days', value: 'week' },
            { label: 'Last Month', value: 'month' },
            { label: 'Last Year', value: 'year' },
            { label: 'Custom', value: 'custom' },
          ].map((option) => (
            <Button
              key={option.value}
              variant={preset === option.value ? 'default' : 'outline'}
              size="sm"
              className={preset === option.value ? 'bg-blue-600 text-white dark:bg-blue-600 dark:text-white' : 'dark:text-slate-300'}
              onClick={() => handlePresetChange(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>

        {/* Custom date inputs */}
        {preset === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t dark:border-slate-700">
            <div>
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">From</label>
              <Input
                type="date"
                value={customStart}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCustomDateChange('start', e.target.value)}
                className="h-9 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">To</label>
              <Input
                type="date"
                value={customEnd}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCustomDateChange('end', e.target.value)}
                className="h-9 text-sm"
              />
            </div>
          </div>
        )}

        {/* Display selected range */}
        <div className="text-xs text-gray-600 dark:text-gray-400 pt-2 border-t dark:border-slate-700">
          <p>
            {format(start, 'MMM dd, yyyy')} → {format(end, 'MMM dd, yyyy')}
          </p>
          <p className="text-gray-500 dark:text-gray-500">
            Duration: {Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))} days
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
